import React, { useState } from 'react';
import { ExternalLink, Copy, Check, ArrowRight, Briefcase, Globe } from 'lucide-react';
import agenciesData from '../data/recruitmentAgencies.json';
import { useTheme } from '../contexts/ThemeContext';

const JobPortalHelper = ({ resumeData }) => {
    const { isDarkMode } = useTheme();
    const portals = agenciesData.job_portals || [];
    const [copiedId, setCopiedId] = useState(null);

    const handleCopy = (text, id) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    // Helper to format text for pasting
    const getExperienceText = () => {
        if (!resumeData?.experience) return '';
        return resumeData.experience.map(exp =>
            `${exp.role} at ${exp.company}\n${exp.date}\n\n${exp.description}`
        ).join('\n\n');
    };

    const getEducationText = () => {
        if (!resumeData?.education) return '';
        return resumeData.education.map(edu =>
            `${edu.degree} - ${edu.school}\n${edu.year}`
        ).join('\n\n');
    };

    const copyItems = [
        { id: 'summary', label: 'Professional Summary', text: resumeData?.personal?.summary || '' },
        { id: 'skills', label: 'Skills (Comma Separated)', text: (resumeData?.skills || []).join(', ') },
        { id: 'experience', label: 'Work Experience (Full Text)', text: getExperienceText() },
        { id: 'education', label: 'Education', text: getEducationText() },
    ];

    return (
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 h-full ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>

            {/* Left: Portals List */}
            <div className={`col-span-1 md:col-span-2 rounded-xl p-6 border ${isDarkMode ? 'bg-slate-800/50 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <Globe className="text-blue-500" />
                    Direct Apply Portals
                </h3>
                <p className={`text-sm mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Click to open the "Upload CV" page for these major portals. Use the Copy Assistant on the right to quickly fill in your details.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {portals.map((portal, index) => (
                        <a
                            key={index}
                            href={portal.url}
                            target="_blank"
                            rel="noreferrer"
                            className={`group p-4 rounded-xl border transition-all flex items-center justify-between hover:scale-[1.02] ${isDarkMode
                                    ? 'bg-slate-800 border-white/5 hover:border-blue-500/50 hover:bg-slate-800/80'
                                    : 'bg-slate-50 border-slate-200 hover:border-blue-400 hover:bg-white hover:shadow-md'
                                }`}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'
                                    }`}>
                                    {portal.name.charAt(0)}
                                </div>
                                <div>
                                    <h4 className="font-bold">{portal.name}</h4>
                                    <span className="text-xs opacity-60 flex items-center gap-1">
                                        <ArrowRight size={10} /> {portal.region}
                                    </span>
                                </div>
                            </div>
                            <ExternalLink size={18} className="opacity-50 group-hover:opacity-100 transition-opacity" />
                        </a>
                    ))}
                </div>
            </div>

            {/* Right: Copy Assistant */}
            <div className={`rounded-xl p-6 border flex flex-col h-full ${isDarkMode ? 'bg-slate-800/50 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <Briefcase className="text-purple-500" />
                    Copy Assistant
                </h3>
                <div className="space-y-4 overflow-y-auto pr-2 custom-scrollbar flex-1">
                    {copyItems.map((item) => (
                        <div key={item.id} className={`p-3 rounded-lg border transition-all ${isDarkMode ? 'bg-slate-900/50 border-white/5 hover:border-white/10' : 'bg-white border-slate-200 hover:border-blue-300'
                            }`}>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-xs font-bold uppercase tracking-wider opacity-60">{item.label}</span>
                                <button
                                    onClick={() => handleCopy(item.text, item.id)}
                                    className={`p-1.5 rounded-md transition-colors ${copiedId === item.id
                                            ? 'bg-green-500/10 text-green-500'
                                            : isDarkMode ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
                                        }`}
                                >
                                    {copiedId === item.id ? <Check size={14} /> : <Copy size={14} />}
                                </button>
                            </div>
                            <div className={`text-xs p-2 rounded max-h-24 overflow-y-auto whitespace-pre-wrap font-mono ${isDarkMode ? 'bg-black/20 text-slate-400' : 'bg-slate-100 text-slate-600'
                                }`}>
                                {item.text || "(Empty)"}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default JobPortalHelper;
